import React, { FC } from 'react'
import { GetServerSideProps } from 'next'
import { ComponentType, LayoutProps, UrlType } from '@sport1/types/web'
import TVLayout from '@/layouts/TV'
import PageContentLayout from '@/layouts/TV/PageContentLayout'
import { getEventStreamLayout } from '@/pages/api/eventStream/[id]'
import TV404 from '@/pages/tv/404'
import Config from '@/utils/Config'
import getJson from '@/utils/fetch/getJson'
import { NavigationItemServer } from '@/utils/navigation/TV'

type EventStreamProps = {
    layoutData?: LayoutProps
    topNavigation?: NavigationItemServer[]
}

type EventStreamComponent = {
    type: ComponentType.TOP_VIDEO
    urlType: UrlType.EVENT_STREAM
    endTime: string
}

const availableFor = 1000 * 60 * 60 * 24

const EventStream: FC<EventStreamProps> = ({ layoutData, topNavigation }) => {
    if (!layoutData) {
        return <TV404 topNavigation={topNavigation || []} />
    }

    return (
        <TVLayout topNavigation={topNavigation || []}>
            <PageContentLayout layoutData={layoutData} />
        </TVLayout>
    )
}

export const getServerSideProps: GetServerSideProps<EventStreamProps> = async context => {
    const eventStreamId = `${context.query.eventStreamId}`

    if (!/^\d+$/.test(eventStreamId)) {
        return { notFound: true }
    }

    const [layoutData, navigationData] = await Promise.all([
        getEventStreamLayout(eventStreamId).catch(() => undefined),
        getJson(`${Config.CMS_API}/v3/de/tv/navigation`).catch(() => undefined),
    ])

    const topNavigation = (navigationData?.topNavigation as NavigationItemServer[]) || null

    if (!layoutData || !navigationData) {
        return { notFound: true, props: { topNavigation } }
    }

    const eventStream = layoutData.components?.find(
        c => c.type === ComponentType.TOP_VIDEO && c.urlType === UrlType.EVENT_STREAM && c.endTime
    ) as EventStreamComponent
    if (eventStream && Date.now() - new Date(eventStream.endTime).getTime() > availableFor) {
        /** for SEO reasons */
        return { notFound: true, props: { topNavigation } }
    }

    const deeplink = `sport1://live-stream/${eventStreamId}`
    context.res.setHeader('deeplink', deeplink)

    layoutData.meta?.tracking?.push({ key: 'page_withvideo', value: 'true' })
    return { props: { layoutData, topNavigation, deeplink } }
}

export default EventStream
